import { useStore } from '@tanstack/react-form'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRight, CheckIcon, X, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'
import zxcvbn from 'zxcvbn'

import { useAppForm } from '@hooks/form'
import useErrorMessage from '@hooks/use-error-message'

import { useMutationPasswordResetReset } from '@api/password-reset/password-reset.mutations'

import FieldErrors from '@/components/form/field-errors'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  email: z.email(),
  mode: z.enum(['set', 'change']).optional().default('change'),
})

const schema = z
  .object({
    password: z
      .string()
      .min(1, { message: 'form.error.mandatory' })
      .min(12, {
        message:
          'session.password-reset.reset.fields.password-rules.min-length',
      })
      .regex(/[A-Z]/, {
        message:
          'session.password-reset.reset.fields.password-rules.at-least-one-uppercase-letter',
      })
      .regex(/[a-z]/, {
        message:
          'session.password-reset.reset.fields.password-rules.at-least-one-lowercase-letter',
      })
      .regex(/[0-9]/, {
        message:
          'session.password-reset.reset.fields.password-rules.at-least-one-number',
      })
      .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, {
        message:
          'session.password-reset.reset.fields.password-rules.at-least-one-special-character',
      })
      .refine((password) => zxcvbn(password).score >= 3, {
        message:
          'session.password-reset.reset.fields.password-rules.at-least-score-3',
      }),
    confirmPassword: z.string().min(1, { message: 'form.error.mandatory' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'session.password-reset.reset.fields.confirm-password.must-match',
  })

export const Route = createFileRoute('/session/password-reset/$token/')({
  component: RouteComponent,
  validateSearch: searchSchema,
  errorComponent: () => {
    const { t } = useTranslation()
    return (
      <div className="flex items-center gap-2">
        <X className="size-4 text-red-500" />
        <span className="text-sm font-semibold">
          {t('session.password-reset.reset.fields.invalid-token')}
        </span>
      </div>
    )
  },
})

function RouteComponent() {
  const { t } = useTranslation()
  const { token } = Route.useParams()
  const { email, mode } = Route.useSearch()
  const { mutate, isPending } = useMutationPasswordResetReset()
  const navigate = useNavigate()
  const errorMessage = useErrorMessage()

  const form = useAppForm({
    defaultValues: { password: '', confirmPassword: '' },
    validators: { onSubmit: schema },
    onSubmit: ({ value: { password }, formApi }) => {
      mutate(
        { token, email, password },
        {
          onSuccess: () => {
            navigate({ to: '/session/login' })
            toast.success(t(`session.password-reset.reset.success.${mode}`))
          },
          onError: (error) => {
            const message = errorMessage({
              error,
              messageByText: {
                'Password is pwned': t(
                  'session.password-reset.reset.fields.password-is-pwned',
                ),
              },
            })
            formApi.setErrorMap({
              onSubmit: {
                form: message,
                fields: {},
              },
            })
          },
        },
      )
    },
  })

  const errors = useStore(form.store, (state) =>
    state.errors.filter((e) => typeof e === 'string'),
  )

  const password = useStore(form.store, (state) => state.values.password)

  return (
    <>
      <h1 className="text-lg font-bold">
        {t(`session.password-reset.reset.title.${mode}`)}
      </h1>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-4"
      >
        <form.AppField name="password">
          {(field) => (
            <field.TextField
              label={t(
                `session.password-reset.reset.fields.password.label.${mode}`,
              )}
              placeholder={t(
                'session.password-reset.reset.fields.password.placeholder',
              )}
              type="password"
            />
          )}
        </form.AppField>
        <form.AppField name="confirmPassword">
          {(field) => (
            <field.TextField
              label={t(
                `session.password-reset.reset.fields.confirm-password.label.${mode}`,
              )}
              placeholder={t(
                'session.password-reset.reset.fields.confirm-password.placeholder',
              )}
              type="password"
            />
          )}
        </form.AppField>
        <PasswordRules password={password} />
        {!!errors.length && <FieldErrors errors={errors} />}
        <form.AppForm>
          <form.SubmitButton pending={isPending} variant="primary" size="sm">
            <div className="flex items-center gap-1">
              <span>{t(`session.password-reset.reset.submit.${mode}`)}</span>
              <ArrowRight className="size-4 rounded-md" />
            </div>
          </form.SubmitButton>
        </form.AppForm>
      </form>
    </>
  )
}

interface PasswordRulesProps {
  password: string
}

const PasswordRules = ({ password }: PasswordRulesProps) => {
  const { t } = useTranslation()
  const passwordScore = zxcvbn(password).score

  return (
    <div className="bg-secondary flex flex-col gap-6 rounded-md border p-4 text-sm font-semibold text-gray-700">
      <div className="flex items-center justify-between gap-2">
        <span className="whitespace-nowrap">
          {t('session.password-reset.reset.fields.password-rules.score')}:{' '}
          {passwordScore}
        </span>
        <div
          className={cn('h-2 w-full rounded-full', {
            'bg-red-300': passwordScore === 0,
            'bg-amber-300': passwordScore === 1,
            'bg-orange-300': passwordScore === 2,
            'bg-lime-300': passwordScore === 3,
            'bg-emerald-300': passwordScore === 4,
          })}
        />
      </div>
      <ul className="flex list-inside list-disc flex-col gap-4">
        {[
          {
            label: t(
              'session.password-reset.reset.fields.password-rules.at-least-score-3',
            ),
            satisfied: passwordScore >= 3,
          },
          {
            label: t(
              'session.password-reset.reset.fields.password-rules.min-length',
            ),
            satisfied: password.length >= 12,
          },
          {
            label: t(
              'session.password-reset.reset.fields.password-rules.at-least-one-uppercase-letter',
            ),
            satisfied: /[A-Z]/.test(password),
          },
          {
            label: t(
              'session.password-reset.reset.fields.password-rules.at-least-one-lowercase-letter',
            ),
            satisfied: /[a-z]/.test(password),
          },
          {
            label: t(
              'session.password-reset.reset.fields.password-rules.at-least-one-number',
            ),
            satisfied: /[0-9]/.test(password),
          },
          {
            label: t(
              'session.password-reset.reset.fields.password-rules.at-least-one-special-character',
            ),
            satisfied: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
          },
        ].map((rule) => (
          <li
            key={rule.label}
            className={cn(
              'flex items-center justify-between gap-1',
              rule.satisfied ? 'text-gray-400' : '',
            )}
          >
            <span>{rule.label}</span>
            {rule.satisfied ? (
              <CheckIcon className="size-4 text-green-500" strokeWidth={3} />
            ) : (
              <XIcon className="size-4 text-red-500" strokeWidth={3} />
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
